import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Loader2, ArrowLeft, AlertTriangle, X, Save } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EditJobPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tradesRes, jobRes] = await Promise.all([
          axios.get(`${API}/trades`),
          axios.get(`${API}/jobs/${id}`)
        ]);
        setTrades(tradesRes.data);
        const job = jobRes.data;
        setFormData({
          title: job.title || '',
          description: job.description || '',
          trade_categories: job.trade_categories?.length > 0 ? job.trade_categories : (job.trade_category ? [job.trade_category] : []),
          location: job.location || '',
          city: job.city || '',
          country: job.country || 'Belgium',
          latitude: job.latitude || '',
          longitude: job.longitude || '',
          start_date: job.start_date || '',
          duration: job.duration || '',
          budget_min: job.budget_min || '',
          budget_max: job.budget_max || '',
          is_urgent: job.is_urgent || false,
          status: job.status || 'open'
        });
      } catch (err) {
        toast.error('Chantier introuvable');
        navigate('/jobs');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const toggleTrade = (tradeId) => {
    setFormData(prev => ({
      ...prev,
      trade_categories: prev.trade_categories.includes(tradeId)
        ? prev.trade_categories.filter(t => t !== tradeId)
        : [...prev.trade_categories, tradeId]
    }));
  };

  const getTradeLabel = (tradeId) => {
    const trade = trades.find(t => t.id === tradeId);
    return trade ? (t(`trades.${trade.id}`) || trade.name_fr) : tradeId;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.trade_categories.length === 0) {
      toast.error('Sélectionnez au moins un corps de métier');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/jobs/${id}`, {
        ...formData,
        trade_category: formData.trade_categories[0],
        budget_min: parseFloat(formData.budget_min) || 0,
        budget_max: parseFloat(formData.budget_max) || 0,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null
      }, { withCredentials: true });
      toast.success('Chantier modifié avec succès');
      navigate(`/jobs/${id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la modification');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !formData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" /> Retour
        </Button>

        <div className="mb-8">
          <h1 className="font-heading font-bold text-3xl text-[#111827]" data-testid="edit-job-title">
            Modifier le chantier
          </h1>
        </div>

        <form onSubmit={handleSubmit}>
          <Card className="mb-6">
            <CardHeader><CardTitle className="font-heading">Informations</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="title">Titre *</Label>
                <Input id="title" name="title" value={formData.title} onChange={handleChange} required className="input-field" data-testid="edit-job-title-input" />
              </div>
              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea id="description" name="description" value={formData.description} onChange={handleChange} required className="min-h-[120px] input-field" data-testid="edit-job-description-input" />
              </div>

              {/* Multi-select trades */}
              <div>
                <Label>Corps de métier(s) *</Label>
                {formData.trade_categories.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2 mb-3" data-testid="edit-selected-trades">
                    {formData.trade_categories.map(tradeId => (
                      <Badge key={tradeId} className="bg-[#1E3A8A] text-white px-3 py-1 gap-1 cursor-pointer hover:bg-[#1E3A8A]/80" onClick={() => toggleTrade(tradeId)}>
                        {getTradeLabel(tradeId)} <X className="w-3 h-3" />
                      </Badge>
                    ))}
                  </div>
                )}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 p-3 border rounded-lg bg-white max-h-[220px] overflow-y-auto">
                  {trades.map(trade => (
                    <label key={trade.id} className={`flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer transition-colors text-sm ${formData.trade_categories.includes(trade.id) ? 'bg-[#1E3A8A]/10 text-[#1E3A8A] font-medium' : 'hover:bg-[#F1F5F9] text-[#374151]'}`}>
                      <Checkbox checked={formData.trade_categories.includes(trade.id)} onCheckedChange={() => toggleTrade(trade.id)} />
                      {t(`trades.${trade.id}`) || trade.name_fr}
                    </label>
                  ))}
                </div>
              </div>

              {/* Status */}
              <div>
                <Label>Statut</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData(prev => ({ ...prev, status: v }))}>
                  <SelectTrigger data-testid="edit-job-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Ouvert</SelectItem>
                    <SelectItem value="closed">Fermé</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between p-4 bg-red-50 border border-red-200 rounded-md">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  <span className="font-medium text-red-700">Marquer comme urgent</span>
                </div>
                <Switch checked={formData.is_urgent} onCheckedChange={(c) => setFormData(prev => ({ ...prev, is_urgent: c }))} data-testid="edit-job-urgent" />
              </div>
            </CardContent>
          </Card>

          <Card className="mb-6">
            <CardHeader><CardTitle className="font-heading">Localisation</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="city">Ville *</Label>
                  <Input id="city" name="city" value={formData.city} onChange={handleChange} required className="input-field" />
                </div>
                <div>
                  <Label>Pays</Label>
                  <Select value={formData.country} onValueChange={(v) => setFormData(prev => ({ ...prev, country: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Belgium">Belgique</SelectItem>
                      <SelectItem value="Luxembourg">Luxembourg</SelectItem>
                      <SelectItem value="France">France</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label htmlFor="location">Adresse</Label>
                <Input id="location" name="location" value={formData.location} onChange={handleChange} className="input-field" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="latitude">Latitude</Label>
                  <Input id="latitude" name="latitude" type="number" step="any" value={formData.latitude} onChange={handleChange} className="input-field" />
                </div>
                <div>
                  <Label htmlFor="longitude">Longitude</Label>
                  <Input id="longitude" name="longitude" type="number" step="any" value={formData.longitude} onChange={handleChange} className="input-field" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="mb-6">
            <CardHeader><CardTitle className="font-heading">Budget & Durée</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="budget_min">Budget min (€)</Label>
                  <Input id="budget_min" name="budget_min" type="number" value={formData.budget_min} onChange={handleChange} className="input-field" />
                </div>
                <div>
                  <Label htmlFor="budget_max">Budget max (€)</Label>
                  <Input id="budget_max" name="budget_max" type="number" value={formData.budget_max} onChange={handleChange} className="input-field" />
                </div>
              </div>
              <div>
                <Label htmlFor="duration">Durée</Label>
                <Input id="duration" name="duration" value={formData.duration} onChange={handleChange} className="input-field" />
              </div>
            </CardContent>
          </Card>

          <Button type="submit" className="btn-primary w-full text-lg py-6 gap-2" disabled={saving} data-testid="save-edit-job">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default EditJobPage;
